import { useQuery } from "@apollo/client";
import {
  Calendar,
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Filter,
  Plus,
} from "lucide-react";
import { DateTime } from "luxon";
import React, { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MultiColumnCalendarGrid } from "@/domains/appointment/appointments/components/calendar/CalendarGrid";
import {
  createTimeSlotConfig,
  getCalendarThemeConfig,
  getTimeColumnWidth,
} from "@/domains/appointment/appointments/config/calendar-config";
import { isToday } from "@/domains/appointment/appointments/utils/calendar-utils";
import { CurrentTimeLine } from "@/domains/appointment/calendar/components/ui/current-time-line";
import { useCalendarStateStore } from "@/domains/appointment/calendar/store/use-calendar-state-store";
import { useWorkersQuery } from "@/domains/workers/hooks/use-workers-query";
import { gql } from "@/gql";
import { useSalonStore } from "@/lib/store/salon";
import { cn } from "@/lib/utils";
import { MonthlyCalendarView } from "./monthly-calendar-view";
import { WeeklyCalendarView } from "./weekly-calendar-view";

// GraphQL query for shifts
const GET_SHIFTS = gql(`
  query GetShifts($filters: ShiftFilterInput!) {
    getShifts(filters: $filters) {
      id
      patternId
      workerSalonId
      shiftDate
      startTime
      endTime
      status
      shiftType
      notes
      replacementWorkerSalonId
      isException
      createdAt
      updatedAt
      breaks {
        id
        shiftId
        breakType
        duration
        startTime
        endTime
        isPaid
        isRequired
        isTaken
      }
    }
  }
`);

interface ShiftCalendarTabProps {
  className?: string;
}

interface ShiftCardProps {
  shift: any;
  worker: any;
  style: React.CSSProperties;
  onClick?: () => void;
}

// Component to render individual shift blocks
function ShiftCard({ shift, worker, style, onClick }: ShiftCardProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case "SCHEDULED":
        return "bg-blue-100 border-blue-300 text-blue-800";
      case "CONFIRMED":
        return "bg-green-100 border-green-300 text-green-800";
      case "IN_PROGRESS":
        return "bg-yellow-100 border-yellow-300 text-yellow-800";
      case "COMPLETED":
        return "bg-gray-100 border-gray-300 text-gray-800";
      case "CANCELLED":
        return "bg-red-100 border-red-300 text-red-800";
      default:
        return "bg-gray-100 border-gray-300 text-gray-800";
    }
  };

  return (
    <div
      className={`absolute left-1 right-1 rounded border-l-4 border cursor-pointer hover:shadow-md transition-shadow p-1 text-xs ${getStatusColor(shift.status)}`}
      style={{
        ...style,
        borderLeftColor: worker.color || "#3b82f6",
        minHeight: "24px",
      }}
      onClick={onClick}
    >
      <div className="font-medium truncate">
        {shift.startTime} - {shift.endTime}
      </div>
      <div className="text-xs opacity-75 truncate">
        {shift.shiftType || "Standard"}
        {shift.isException && " (Exception)"}
      </div>
      {shift.notes && <div className="text-xs opacity-60 truncate mt-1">{shift.notes}</div>}
    </div>
  );
}

// Comprehensive Shift Calendar View
function ShiftCalendarView({ currentDate }: { currentDate: DateTime }) {
  const currentSalon = useSalonStore((state) => state.currentSalon);
  const { timeScale } = useCalendarStateStore();

  // Fetch workers
  const { data: workersData, loading: workersLoading } = useWorkersQuery({
    salonId: currentSalon?.id,
  });

  // Fetch shifts for the current day
  const { data: shiftsData, loading: shiftsLoading } = useQuery(GET_SHIFTS, {
    variables: {
      filters: {
        franchiseId: currentSalon?.franchiseId,
        startDate: currentDate.startOf("day").toISODate(),
        endDate: currentDate.endOf("day").toISODate(),
      },
    },
    skip: !currentSalon?.franchiseId,
  });

  // Configuration for calendar layout
  const scaleConfig = getCalendarThemeConfig(timeScale);
  const timeConfig = createTimeSlotConfig(0, 24, {
    hourHeight: scaleConfig.layout.hourHeight,
  });

  // Process workers - only show active workers, no unassigned column
  const processedWorkers = useMemo(() => {
    const workers = workersData?.workers?.workers || [];
    const activeWorkers = workers.filter((worker) => worker.isActive);
    return activeWorkers;
  }, [workersData]);

  // Process shifts and organize by worker - only include shifts for active workers
  const shiftsByWorker = useMemo(() => {
    if (!shiftsData?.getShifts) return {};

    const shifts = shiftsData.getShifts;
    const result: Record<string, any[]> = {};

    // Create mapping of workerSalonId to workerId
    const workerSalonToWorkerMap: Record<string, string> = {};
    processedWorkers.forEach((worker) => {
      result[worker.id] = [];
      // Map each workerSalon.id to the worker.id
      worker.workerSalons?.forEach((workerSalon) => {
        workerSalonToWorkerMap[workerSalon.id] = worker.id;
      });
    });

    shifts.forEach((shift) => {
      const workerSalonId = shift.workerSalonId;
      const workerId = workerSalonToWorkerMap[workerSalonId];
      // Only include shifts that have a mapped worker
      if (workerId && result[workerId]) {
        // Calculate position based on time
        const [startHour, startMinute] = shift.startTime.split(":").map(Number);
        const [endHour, endMinute] = shift.endTime.split(":").map(Number);

        const startTotalMinutes = startHour * 60 + startMinute;
        const endTotalMinutes = endHour * 60 + endMinute;

        const top = (startTotalMinutes / 60) * scaleConfig.layout.hourHeight;
        const height = ((endTotalMinutes - startTotalMinutes) / 60) * scaleConfig.layout.hourHeight;

        result[workerId].push({
          ...shift,
          style: {
            top: `${top}px`,
            height: `${Math.max(height, 24)}px`,
            zIndex: 10,
          },
        });
      }
    });

    return result;
  }, [shiftsData, processedWorkers, scaleConfig]);

  const handleShiftClick = (_shift: any) => {
    // TODO: Open shift edit dialog
  };

  const renderWorkerColumn = (worker: any, workerIndex: number) => {
    const workerShifts = shiftsByWorker[worker.id] || [];
    const showCurrentTimeLine = isToday(currentDate);
    const workerColor = worker.color || "#6b7280";

    return (
      <div key={worker.id} className="border-r border-border last:border-r-0 flex-1 min-w-[160px]">
        {/* Worker Header */}
        <div className="h-12 border-b border-border flex items-center justify-center px-2 bg-muted">
          <div className="flex items-center space-x-2 min-w-0">
            <div
              className="w-3 h-3 rounded-full border border-white flex-shrink-0"
              style={{ backgroundColor: workerColor }}
            />
            <span className="text-xs font-medium truncate text-foreground">
              {`${worker.user?.firstName || ""} ${worker.user?.lastName || ""}`.trim()}
            </span>
          </div>
        </div>

        {/* Time Slots Column */}
        <div className="relative" style={{ minHeight: `${24 * scaleConfig.layout.hourHeight}px` }}>
          {/* Hour Dividers */}
          {Array.from({ length: 24 }).map((_, hour) => (
            <div
              key={hour}
              className="border-b border-border dark:border-border last:border-b-0"
              style={{ height: `${scaleConfig.layout.hourHeight}px` }}
            />
          ))}

          {/* 15-minute interval lines */}
          {Array.from({ length: 24 * 4 }).map((_, interval) => {
            if (interval % 4 === 0) return null;
            return (
              <div
                key={`interval-${interval}`}
                className="absolute left-0 right-0 border-b border-border/30 dark:border-border/20 pointer-events-none"
                style={{
                  top: `${interval * (scaleConfig.layout.hourHeight / 4)}px`,
                  zIndex: 1,
                }}
              />
            );
          })}

          {/* Empty state for workers with no shifts */}
          {workerShifts.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
              <div className="text-center p-4">
                <div className="text-xs text-muted-foreground mb-1">No shifts scheduled</div>
                <div className="text-xs text-muted-foreground">for this day</div>
              </div>
            </div>
          )}

          {/* Current Time Line */}
          {showCurrentTimeLine && (
            <CurrentTimeLine
              startHour={0}
              endHour={24}
              hourHeight={scaleConfig.layout.hourHeight}
              showTimeLabel={workerIndex === 0}
            />
          )}

          {/* Shift Blocks */}
          {workerShifts.map((shift, index) => (
            <ShiftCard
              key={`${shift.id}-${index}`}
              shift={shift}
              worker={worker}
              style={shift.style}
              onClick={() => handleShiftClick(shift)}
            />
          ))}
        </div>
      </div>
    );
  };

  if (shiftsLoading || workersLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading shift calendar...</div>
      </div>
    );
  }

  if (processedWorkers.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-muted-foreground mb-2">No active workers found</div>
          <div className="text-sm text-muted-foreground">
            Make sure workers are assigned to this salon and are active.
          </div>
        </div>
      </div>
    );
  }

  return (
    <MultiColumnCalendarGrid
      timeConfig={timeConfig}
      numberOfColumns={processedWorkers.length}
      containerClassName=""
      className=""
      timeColumnWidth={getTimeColumnWidth()}
      columnWidth="auto"
      minHeight="auto"
      enableHorizontalScroll={true}
      responsiveColumns={true}
    >
      {processedWorkers.map(renderWorkerColumn)}
    </MultiColumnCalendarGrid>
  );
}

// Main ShiftCalendar component with date navigation
function ShiftCalendar() {
  const currentSalon = useSalonStore((state) => state.currentSalon);
  const [currentDate, setCurrentDate] = useState(() => DateTime.now());

  const handlePreviousDay = () => {
    setCurrentDate((prev) => prev.minus({ days: 1 }));
  };

  const handleNextDay = () => {
    setCurrentDate((prev) => prev.plus({ days: 1 }));
  };

  const handleToday = () => {
    setCurrentDate(DateTime.now());
  };

  if (!currentSalon) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <CalendarIcon className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">Please select a salon to view shifts</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <CardTitle className="text-lg">
              Shift Calendar - {currentDate.toLocaleString(DateTime.DATE_FULL)}
            </CardTitle>
            {isToday(currentDate) && (
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                Today
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handlePreviousDay}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={handleToday}>
              Today
            </Button>
            <Button variant="outline" size="sm" onClick={handleNextDay}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        <ShiftCalendarView currentDate={currentDate} />
      </CardContent>
    </Card>
  );
}

export function ShiftCalendarTab({ className = "" }: ShiftCalendarTabProps) {
  const currentSalon = useSalonStore((state) => state.currentSalon);
  const [viewMode, setViewMode] = useState<"daily" | "weekly" | "monthly">("daily");

  // Fetch some basic stats for the current salon
  const { data: workersData } = useWorkersQuery({
    salonId: currentSalon?.id,
  });

  const activeWorkers = workersData?.workers?.workers?.filter((w) => w.isActive) || [];
  const activeWorkerCount = activeWorkers.length;

  if (!currentSalon) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <CalendarIcon className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">
            Please select a salon to view shift calendar
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("space-y-6", className)}>
      {/* Tab Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Shift Calendar</h1>
          <p className="text-muted-foreground">
            View and manage daily shift schedules in calendar format
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* View Mode Toggle */}
          <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
            <Button
              variant={viewMode === "daily" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("daily")}
              className="h-8"
            >
              Daily
            </Button>
            <Button
              variant={viewMode === "weekly" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("weekly")}
              className="h-8"
            >
              Weekly
            </Button>
            <Button
              variant={viewMode === "monthly" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("monthly")}
              className="h-8"
            >
              Monthly
            </Button>
          </div>

          {/* Action Buttons */}
          <Button variant="outline" size="sm">
            <Filter className="w-4 h-4 mr-2" />
            Filter
          </Button>
          <Button size="sm">
            <Plus className="w-4 h-4 mr-2" />
            Add Shift
          </Button>
        </div>
      </div>

      {/* Calendar Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Workers</p>
                <p className="text-2xl font-bold">{activeWorkerCount}</p>
              </div>
              <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                Available
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Today's Shifts</p>
                <p className="text-2xl font-bold">-</p>
              </div>
              <Badge variant="secondary" className="bg-green-100 text-green-800">
                Scheduled
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Hours</p>
                <p className="text-2xl font-bold">-</p>
              </div>
              <Badge variant="secondary" className="bg-purple-100 text-purple-800">
                Hours
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Coverage</p>
                <p className="text-2xl font-bold">-</p>
              </div>
              <Badge variant="secondary" className="bg-orange-100 text-orange-800">
                Complete
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Calendar Component */}
      {viewMode === "daily" && <ShiftCalendar />}
      {viewMode === "weekly" && <WeeklyCalendarView />}
      {viewMode === "monthly" && <MonthlyCalendarView />}

      {/* Four-Layer System Info */}
      <Card className="border-green-200 bg-green-50/50 dark:border-green-800 dark:bg-green-950/20">
        <CardHeader className="pb-3">
          <CardTitle className="text-green-900 dark:text-green-100 text-base">
            Layer 4: Shift Calendar View
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-green-800 dark:text-green-200">
              <Calendar className="w-4 h-4" />
              <span className="font-medium">Daily Schedule Management</span>
            </div>
            <p className="text-green-700 dark:text-green-300 text-xs ml-6">
              View actual shifts generated from patterns. Each shift represents a specific work
              assignment for a worker on a specific day and time.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
